import api from './api';
import { searchLocations } from './locations.service';

export type CarrierType = 'SCAC' | 'RAIL';

export interface AllowlistEntry {
  id: number;
  carrier_type: CarrierType;
  carrier_code: string;
  enabled: boolean;
  max_imports: number | null;
}

export interface AllowlistEntryCreate {
  carrier_type: CarrierType;
  carrier_code: string;
  enabled: boolean;
  max_imports?: number | null;
}

export interface AllowlistEntryUpdate {
  enabled?: boolean;
  carrier_code?: string;
  max_imports?: number | null;
}

// gwiTrace's catalog of known carriers, surfaced through the
// /tracking/available-carriers proxy. Used by the Allowed Carriers
// admin page to populate the Add dropdown with concrete choices
// instead of a free-text input.
export interface AvailableCarrier {
  code: string;
  name: string;
  carrier_type: CarrierType;           // mapped (steamship_line→SCAC; rail+terminal→RAIL)
  gwitrace_type: 'steamship_line' | 'rail' | 'terminal';
  is_active: boolean;
}

// CSX-terminal-mapping admin types. CSX is the one rail carrier where
// gwiTrace requires a terminal scope on the import; the sweep resolves
// each order's pickup location (cms.legscurrent.origcode → locations
// .recno) to a canonical CSX terminal via this mapping.
export interface CsxTerminalMapping {
  location_recno: number;
  csx_terminal: string;
  location_lname: string | null;
  location_lcity: string | null;
  location_state: string | null;
}

export interface CmsLocationOption {
  recno: number;
  lname: string | null;
  lcity: string | null;
  state: string | null;
}

// One row per stale tracking link (Tracing → Not Tracking page).
// "Stale" = last_synced_at NULL or older than `hours` (default 48).
// Outgated and trace-terminated orders are excluded by the backend.
export interface StaleLinkRow {
  pronumber: number;
  shipment_id: string;
  container: string | null;
  bol: string | null;
  scac: string | null;
  rail: string | null;
  movetype: string | null;
  last_synced_at: string | null;
  gwitrace_last_carrier_data_at: string | null;
  last_error: string | null;
  created_at: string | null;
}

export interface StaleLinksResponse {
  hours_threshold: number;
  count: number;
  rows: StaleLinkRow[];
}

// CSX import orders the register sweep skips because it can't resolve a
// CSX terminal — they never get a tracking link, so they don't show in
// stale-links. Bucketed by cause so the UI can route the fix.
export interface UntrackedCsxRow {
  pronumber: number;
  container: string | null;
  bol: string | null;
  scac: string | null;
  reason: 'needs_origin_location' | 'needs_mapping';
  pickup_location_recno: number | null;
  location_name?: string | null;
  location_city?: string | null;
  location_state?: string | null;
}

export interface UntrackedCsxResponse {
  needs_mapping: UntrackedCsxRow[];
  needs_origin_location: UntrackedCsxRow[];
  count: number;
}

// One row per (webhook, pronumber) pair. A single inbound webhook with
// N linked pronumbers produces N rows; pre-fan-out failures (HMAC
// rejected, bad payload, no_link) produce one row with pronumber=null.
// fields_updated is the comma-separated CMS column names actually
// written for that pronumber by sync_pronumber.
// 7-day retention on the backend.
export type WebhookLogStatus =
  | 'success'
  | 'no_changes'
  | 'error'
  | 'no_link'
  | 'signature_rejected'
  | 'invalid_payload'
  | 'ignored';

export interface WebhookLogRow {
  id: number;
  received_at: string;
  event: string | null;
  shipment_id: string | null;
  container_number: string | null;     // captured from the snapshot at receive time
  pronumber: number | null;
  fields_updated: string | null;       // comma-separated CMS column names
  field_count: number;
  status: WebhookLogStatus;
  error_message: string | null;
  duration_ms: number | null;
  signature_valid: boolean;
  // Order context from ordersall (LEFT JOINed on pronumber). NULL when the
  // webhook row has no pronumber (no_link / sig_rejected / invalid_payload)
  // or when no matching order exists.
  order_rail: string | null;
  order_scac: string | null;
}

export interface WebhookLogResponse {
  total: number;
  limit: number;
  offset: number;
  rows: WebhookLogRow[];
}


// --- Export Carriers page (Tracing -> Export Carriers) -----------------
// Per-TERMINAL opt-in for EXPORT tracing. A separate table and page from
// Allowed Carriers: the import allowlist is read by six import-live modules,
// so widening its shape to carry a direction would put a schema change
// underneath production import tracing.
//
// The cap counts BOOKINGS, not entries. One booking becomes N gwiTrace
// entries -- one per CMS PRO, ~2.8 on average and up to 30 -- so a cap in
// entries would mean something different for every booking.
export interface ExportAllowlistRow {
  id: number;
  carrier_code: string;
  enabled: boolean;
  max_bookings: number | null;   // null = unlimited
  tracked_bookings: number;
  tracked_entries: number;
}

export interface ExportTerminalChoice {
  code: string;
  name: string | null;
  is_active: boolean;
}

// `status` is deliberately richer than ok/failed. "CMS has no open export
// order with this booking" is not an error, but it IS something the operator
// must act on -- a bare "0 registered" would not say which.
export type ExportRegisterStatus =
  | 'ok' | 'no_orders' | 'no_eligible' | 'failed' | 'disabled' | 'invalid';

export interface ExportRegisterResult {
  booking: string;
  status: ExportRegisterStatus;
  detail: string;
  registered: number;
  pros: number;
  errors: number;
}

// --- Tracking Links page (Tracing -> Tracking Links) -------------------
// One order_tracking_link row plus a LIVE check of whether its gwiTrace
// shipment still exists. "orphaned" is the only deletable state: the
// shipment is gone, so the link points at nothing. "unchecked" means the
// existence probe failed - never deletable, so a gwiTrace outage can't
// present live links as safe to remove.
export type TrackingLinkStatus = 'orphaned' | 'linked' | 'unchecked';

export interface TrackingLinkRow {
  pronumber: number;
  gwitrace_shipment_id: string;
  submitted_container: string | null;
  submitted_bill_of_lading: string | null;
  /** The EXPORT counterpart of the B/L. Mutually exclusive with it -- the page
   *  renders one "BOL / BKG" column from the two. */
  submitted_booking_number: string | null;
  submitted_scac: string | null;
  submitted_rail: string | null;
  /** CMS movetype joined from `orderscurrent`. Null = the PRO is no longer
   *  in orderscurrent (deleted / archived), which is itself worth seeing. */
  movetype: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  status: TrackingLinkStatus;
}

export interface TrackingLinksResponse {
  total: number;          // rows matching the FILTERS, before status filtering
  returned: number;       // rows on this page after status filtering
  page_size: number;
  offset: number;
  probe_failed: boolean;
  links: TrackingLinkRow[];
}

export interface TrackingLinksQuery {
  carrier?: string;       // matched against BOTH scac and rail columns
  containers?: string;    // one, or many separated by comma/space/newline
  status?: 'all' | 'orphaned' | 'linked';
  limit?: number;
  offset?: number;
}

export interface TrackingLinkDeleteResult {
  deleted: number[];
  still_linked: number[]; // refused - shipment still alive in gwiTrace
  not_found: number[];
}

export interface WebhookLogQuery {
  since?: string;             // ISO datetime
  until?: string;
  pronumber?: number;
  shipment_id?: string;
  event?: string;
  status?: WebhookLogStatus;
  limit?: number;
  offset?: number;
}

// --- Click-trace / bulk Trace All ------------------------------------
// A trace routes to gwiTrace (allowlisted carriers) or legacy (everything
// else — Gnosis/UPRR/BNSF exports/HOU terminals, reported as not-yet-wired
// in phase 1). Single-PRO and bulk share the same result shape.
export type TraceMode = 'gwitrace' | 'legacy' | 'none';
export type TraceStatus =
  | 'updated'
  | 'queued'
  | 'not_wired'
  | 'not_traceable'
  | 'not_available'
  | 'error'
  /** The stored gwiTrace snapshot the popup opens on at once, while the real
   *  trace asks the port. Never a verdict; the real result replaces it. */
  | 'stored'
  /** The port answered and has no such booking (BOOKING_NOT_FOUND). The row's
   *  summary is the badge text, e.g. "HOU-BPT has no booking '58672276'". */
  | 'not_found'
  /** This click registered the booking with gwiTrace and queued its first
   *  sweep. Was "Queued" / "No update", which read as nothing happening. */
  | 'registered'
  /** POH has the booking / box at the OTHER Houston terminal from the one the
   *  trace was run as. The popup shows only `summary`, e.g. "Booking 279448109
   *  is not at Bayport (HOU-BPT). It is at Barbours Cut (HOU-BCT)." — the cue
   *  LYNX gave by returning nothing. The CMS write and the overlay still ran. */
  | 'wrong_terminal';

// gwiTrace shipment snapshot — a permissive subset of the fields the trace
// panel renders (mirrors v1 gwitraceinfo). Only present for gwiTrace traces
// that returned a stored snapshot; everything is optional.
export interface TraceSnapshot {
  id?: string;
  lifecycle_state?: string;
  current_location?: string;
  vessel_name?: string;
  voyage_number?: string;
  pol_locode?: string;
  pod_locode?: string;
  vessel_departed_at?: string;
  vessel_arrived_at?: string;
  vessel_estimated_arrival_at?: string;
  estimated_arrival_at?: string;
  terminal_name?: string;
  terminal_code?: string;
  grounded_at?: string;
  grounded_spot?: string;
  gated_out_at?: string;
  last_free_day?: string;
  customs_status?: string;
  line_status?: string;
  at_other_hold?: string | boolean;
  chassis_number?: string;
  seal_number?: string;
  is_on_rail?: boolean;
  is_available_for_pickup?: boolean;
  providers?: Record<
    string,
    {
      last_success_at?: string | null;
      is_receiving_data?: boolean;
      last_error?: string | null;
      provider_type?: string;
    }
  >;
  milestones?: Array<{
    occurred_at?: string;
    location_code?: string;
    source_provider?: string;
    label?: string;
    code?: string;
  }>;
  errors?: Array<{ code?: string; severity?: string; message?: string }>;
  /** Active gwiTrace exceptions — holds, LFD past due, customs notes, exam in
   *  progress. gwiTrace DROPS an entry when it resolves (no resolved flag on
   *  the row), so everything present here is currently active and renders. */
  exceptions?: Array<{
    code?: string;
    severity?: string;
    message?: string;
    detected_at?: string;
  }>;
  /** {provider_type: carrier_code}, e.g. {terminal: 'HOU-BCT'}. */
  carriers?: Record<string, string> | null;
  /** EXPORT booking section. FLAT on the booking placeholder (the sweep writes
   *  the schedule there); on a container row the schedule sits in the
   *  `terminal` drawer and vessel/cutoffs are promoted to the top level. */
  export?: Record<string, unknown> | null;
  /** EXPORT, click-trace only: the booking's container rows (separate
   *  gwiTrace shipments), attached by gwi360's trace_linked for the popup's
   *  "Equipment In For Booking" table. */
  booking_units?: Array<{
    container_number?: string | null;
    equipment_type?: string | null;
    lifecycle_state?: string | null;
    current_location?: string | null;
    grounded_spot?: string | null;
  }>;
  [key: string]: unknown;
}

export interface TraceResult {
  pronumber: number;
  // Equipment / container number, shown next to the PRO in the results panel.
  container?: string | null;
  mode: TraceMode;
  status: TraceStatus;
  summary: string;
  fields_updated: string[];
  // Every CMS column the trace evaluated, changed or not (gwiTrace traces).
  fields_evaluated?: string[];
  shipment_id: string | null;
  snapshot: TraceSnapshot | null;
  last_synced_at: string | null;
  // Legacy (cmsapi/trace.pl) single-trace result — refreshed CMS trace fields.
  trace_fields: TraceStatusRow | null;
  // Snapshot-grade legacy payload (gnosis mapped fields + raw results), when
  // the carrier's tracer emits a [tracejson] block. Null otherwise.
  trace_data: LegacyTraceData | null;
  // After a data-changing trace: fresh order-model values for the changed
  // fields (keyed by OrderDetails camelCase key, formatted like the screen)
  // plus the list of changed keys — so the screen can overlay + highlight them
  // in place, without a re-fetch (unsaved edits survive).
  updated_order_fields?: Record<string, unknown>;
  updated_keys?: string[];
  // Legacy "queued" only: the order's Trace Date captured before the fork fired.
  // The watch-progress poll flips the row to done once trace-status reports a
  // different Trace Date (trace.pl stamped a fresh run) — the reliable per-PRO
  // completion signal for fire-and-forget legacy traces.
  prev_trace_dt?: string | null;
  // Status 'wrong_terminal' only: the terminal the trace was run as and where
  // POH actually has the booking (kind 'booking') or box (kind 'container').
  wrong_terminal?: {
    kind: 'booking' | 'container';
    reference: string;
    traced_as: string;
    actual: string;
    message: string;
  } | null;
  // gwiTrace results: the carrier the trace was RUN as (the popup's "Traced
  // on" / "Trace Carrier"; was gwiTrace's registration, so a Bayport pick read
  // "Traced on HOU-BCT").
  traced_as?: string | null;
  // gwiTrace results where POH has named the Houston terminal: POH's terminal
  // against the order's SAVED Terminal. `message` is the warning band ("Booking
  // X is at Bayport (HOU-BPT), but the order's Terminal is currently saved as
  // Barbours Cut (HOU-BCT).") and null when the two agree; either way the popup
  // shows it in place of gwiTrace's own terminal badge (see `shownExceptions`).
  terminal_check?: {
    code: string;
    saved: string;
    actual: string;
    message: string | null;
  } | null;
  // gwiTrace results on a POH box: POH's ISO code decoded through the table
  // that writes the order's Size / Type ("40' HIGHCUBE (ISO 45G1)"), or
  // "ISO 45GA — not mapped". Null for any other box.
  size_type?: string | null;
  // #7940: a POH port-LFD customer (Caterpillar 5517, Home Depot, Dal Tile)
  // reads POH's PORT date, while gwiTrace's own LFD is the LINE date. This is
  // the port date for the popup's Last Free Day ('' = POH has none). Null or
  // absent for everyone else, whose popup keeps the snapshot's date.
  port_lfd?: string | null;
}

// Legacy trace snapshot from cmsapi (CMS-column read-back + optional raw
// gnosis results). `updated` lists the CMS columns this trace actually changed
// (before/after diff) — the panel highlights them.
export interface LegacyTraceData {
  fields?: Record<string, string | null>;
  updated?: string[];
  raw?: unknown;
  export_detail?: LegacyExportDetail | null;
  // gnosis export Container Info rows (Equip# / Size / Type / Gate Out / Gate In).
  containers?: LegacyTraceContainer[] | null;
}

export interface LegacyTraceContainer {
  equipment?: string | null;
  size?: string | number | null;
  type?: string | null;
  gateout?: string | null;
  gatein?: string | null;
}

// POHA export click-trace booking detail — the v1 tracewin popup's sections
// (booking header + Vessel Information + equipment tables), emitted structured
// by pohaexport.pl so the panel can render them as tables.
export interface LegacyExportDetail {
  category?: string | null;
  booking?: string | null;
  line?: string | null;
  hazmat?: string | null;
  preadvise?: string | null;
  preadvisedate?: string | null;
  notes?: string | null;
  vesselinfo?: {
    vessel?: string | null;
    voyage?: string | null;
    terminal?: string | null;
    status?: string | null;
    pol?: string | null;
    pod?: string | null;
    beginrcv?: string | null;
    drycutoff?: string | null;
    hazcutoff?: string | null;
    reefercutoff?: string | null;
  } | null;
  equipinfo?: {
    qty?: number | string | null;
    mty?: number | string | null;
    rcvd?: number | string | null;
    size?: string | null;
    type?: string | null;
  }[];
  equipin?: {
    equipment?: string | null;
    size?: string | null;
    type?: string | null;
    position?: string | null;
  }[];
}

// scac = "Trace All on SSLine"; rail = "Trace All on Terminal".
export type TraceCarrierType = 'scac' | 'rail';

export interface TraceAllResponse {
  requested: number;
  tracetype?: TraceCarrierType;
  counts: Record<string, number>;
  results: TraceResult[];
}

// Current CMS trace fields for a PRO — polled during legacy Trace All to show
// results landing as trace.pl completes (legacy has no webhook push).
export interface TraceStatusRow {
  traceRunCarrier: string | null;
  traceRunDatetime: string | null;
  traceEvent: string | null;
  traceLocation: string | null;
  traceEventDatetime: string | null;
  traceTerminateDatetime: string | null;
}

class TrackingService {
  private base = '/tracking';

  async listAllowlist(): Promise<AllowlistEntry[]> {
    const resp = await api.get(`${this.base}/allowlist`);
    return resp.data;
  }

  async createAllowlistEntry(payload: AllowlistEntryCreate): Promise<AllowlistEntry> {
    const resp = await api.post(`${this.base}/allowlist`, payload);
    return resp.data;
  }

  async updateAllowlistEntry(id: number, payload: AllowlistEntryUpdate): Promise<AllowlistEntry> {
    const resp = await api.patch(`${this.base}/allowlist/${id}`, payload);
    return resp.data;
  }

  async deleteAllowlistEntry(id: number): Promise<void> {
    await api.delete(`${this.base}/allowlist/${id}`);
  }

  async listAvailableCarriers(): Promise<AvailableCarrier[]> {
    const resp = await api.get(`${this.base}/available-carriers`);
    return resp.data;
  }

  // --- CSX terminal mappings --------------------------------------------

  async listCsxTerminalMappings(): Promise<CsxTerminalMapping[]> {
    const resp = await api.get(`${this.base}/csx-terminal-mappings`);
    return resp.data;
  }

  async listCsxTerminalNames(): Promise<string[]> {
    const resp = await api.get(`${this.base}/csx-terminals`);
    return resp.data;
  }

  async createCsxTerminalMapping(payload: {
    location_recno: number;
    csx_terminal: string;
  }): Promise<CsxTerminalMapping> {
    const resp = await api.post(`${this.base}/csx-terminal-mappings`, payload);
    return resp.data;
  }

  async updateCsxTerminalMapping(
    locationRecno: number,
    csxTerminal: string,
  ): Promise<CsxTerminalMapping> {
    const resp = await api.patch(
      `${this.base}/csx-terminal-mappings/${locationRecno}`,
      { csx_terminal: csxTerminal },
    );
    return resp.data;
  }

  async deleteCsxTerminalMapping(locationRecno: number): Promise<void> {
    await api.delete(`${this.base}/csx-terminal-mappings/${locationRecno}`);
  }

  /** Location typeahead for the CSX terminal-mapping picker. Rides the shared
   *  /admin/locations?q= search (name / alias / city / address / recno) —
   *  the old dedicated /tracking/locations/search endpoint was removed. */
  async searchCmsLocations(q: string): Promise<CmsLocationOption[]> {
    const locs = await searchLocations(q, { limit: 25 });
    return locs.map((l) => ({
      recno: l.recno,
      lname: l.lname,
      lcity: l.lcity,
      state: l.lstate,
    }));
  }

  async refreshPronumber(pronumber: number): Promise<{ pronumber: number; status: string }> {
    const resp = await api.post(`${this.base}/refresh/${pronumber}`);
    return resp.data;
  }

  // --- Not Tracking page -----------------------------------------------

  async listStaleLinks(hours: number = 48, limit: number = 200): Promise<StaleLinksResponse> {
    const resp = await api.get(`${this.base}/stale-links`, {
      params: { hours, limit },
    });
    return resp.data;
  }

  async forceRepoll(pronumber: number): Promise<{ pronumber: number; status: string }> {
    const resp = await api.post(`${this.base}/force-repoll/${pronumber}`);
    return resp.data;
  }

  // --- Click-trace / bulk Trace All ------------------------------------

  /** Click-trace a single PRO; returns its routed trace result. `tracetype`
   *  optionally forces the legacy carrier side (scac=SSLine, rail=Terminal) —
   *  the per-field trace buttons pass it; omitted = backend auto-detects.
   *  `carrier` optionally overrides the carrier to trace with the value
   *  currently selected in the field (which may be an unsaved edit), so the
   *  legacy trace honors the on-screen Terminal/SSLine instead of the persisted
   *  order value. Omitted = backend uses the saved order carrier. */
  async trace(
    pronumber: number,
    tracetype?: TraceCarrierType,
    carrier?: string,
  ): Promise<TraceResult> {
    const params: Record<string, string> = {};
    if (tracetype) params.tracetype = tracetype;
    const c = carrier?.trim();
    if (c) params.carrier = c;
    const resp = await api.post(`${this.base}/trace/${pronumber}`, undefined, {
      params: Object.keys(params).length ? params : undefined,
    });
    return resp.data;
  }

  /** Bulk "Trace All on SSLine (scac) / on Terminal (rail)" for the PROs in view. */
  /** The STORED gwiTrace snapshot for a linked order, at once: no port call,
   *  no CMS write. The Trace button fires this together with `trace()` so the
   *  popup opens on the last sync and the port's answer replaces it in place.
   *  `mode === 'none'` means nothing is stored (not linked); the button then
   *  just waits for the real trace. */
  async traceStored(pronumber: number): Promise<TraceResult> {
    const resp = await api.post(`${this.base}/trace/${pronumber}`, undefined, {
      params: { stored: 'true' },
    });
    return resp.data;
  }

  async traceAll(pronumbers: number[], tracetype: TraceCarrierType): Promise<TraceAllResponse> {
    const resp = await api.post(`${this.base}/trace-all`, { pronumbers, tracetype });
    return resp.data;
  }

  /** Current CMS trace fields per PRO — for the legacy Trace All progress poll. */
  async traceStatus(pronumbers: number[]): Promise<Record<number, TraceStatusRow>> {
    const resp = await api.post(`${this.base}/trace-status`, { pronumbers });
    return resp.data?.statuses ?? {};
  }

  async listUntrackedCsx(): Promise<UntrackedCsxResponse> {
    const resp = await api.get(`${this.base}/untracked-csx`);
    return resp.data;
  }

  // --- Webhook Activity page -------------------------------------------

  async listWebhookLog(q: WebhookLogQuery = {}): Promise<WebhookLogResponse> {
    const resp = await api.get(`${this.base}/webhook-log`, { params: q });
    return resp.data;
  }

  // --- Tracking Links page ---------------------------------------------

  /** Link rows + a live gwiTrace existence check on the returned page. */
  async listTrackingLinks(q: TrackingLinksQuery = {}): Promise<TrackingLinksResponse> {
    const resp = await api.get(`${this.base}/links`, { params: q });
    return resp.data;
  }

  /**
   * Delete ORPHANED link rows (shipment already gone from gwiTrace).
   * The backend re-verifies against gwiTrace and refuses any row whose
   * shipment is still alive, returning those in `still_linked`.
   */
  async deleteTrackingLinks(pronumbers: number[]): Promise<TrackingLinkDeleteResult> {
    const resp = await api.post(`${this.base}/links/delete`, { pronumbers });
    return resp.data;
  }

  // --- Export Carriers page --------------------------------------------

  async listExportAllowlist(): Promise<ExportAllowlistRow[]> {
    const resp = await api.get(`${this.base}/export/allowlist`);
    return resp.data;
  }

  /** Terminals gwiTrace can trace an export against -- the Add dropdown. */
  async listExportTerminals(): Promise<ExportTerminalChoice[]> {
    const resp = await api.get(`${this.base}/export/terminals`);
    return resp.data;
  }

  async createExportAllowlist(
    body: { carrier_code: string; enabled?: boolean; max_bookings?: number | null },
  ): Promise<ExportAllowlistRow> {
    const resp = await api.post(`${this.base}/export/allowlist`, body);
    return resp.data;
  }

  async updateExportAllowlist(
    id: number, body: { enabled?: boolean; max_bookings?: number | null },
  ): Promise<ExportAllowlistRow> {
    const resp = await api.patch(`${this.base}/export/allowlist/${id}`, body);
    return resp.data;
  }

  async deleteExportAllowlist(id: number): Promise<void> {
    await api.delete(`${this.base}/export/allowlist/${id}`);
  }

  /**
   * Register ONE known booking: every open CMS PRO on it becomes a gwiTrace
   * entry. Idempotent -- gwiTrace dedupes on (booking, pronumber), so pressing
   * twice is safe. Same code path the bulk sweep will use.
   */
  async registerExportBooking(booking_number: string): Promise<ExportRegisterResult> {
    const resp = await api.post(`${this.base}/export/register`, { booking_number });
    return resp.data;
  }

}

const trackingService = new TrackingService();
export default trackingService;
